"use client";

import { useEffect, type ReactNode } from "react";
import { cleanCode, itemsCurrency, scrubText, type AnalyticsItem, type CodeSearchResult, type EventMap } from "./events";
import { onPage, openPreferences, rememberCodeSearch, takeCodeSearch, track } from "./runtime";

// Sayfaya bağlı olaylar (sunucu bileşenleri yerleştirir; hiçbir şey çizmez). Olay, sayfa görüntülemesinden sonra ve
// yalnızca analitik onayı varsa gönderilir; onay sayfa açıkken verilirse o anda (runtime.onPage). Aynı sayfada aynı
// olay bir kez. Analitik kapalıysa (AnalyticsRoot yok) runtime yapılandırılmamış olduğundan hiçbir şey yapılmaz.

/** Tur detayı: view_item (+ ana sayfadan kodla gelindiyse tour_code_search exact/departure) */
export function TrackTourView({ item, departureCount }: { item: AnalyticsItem; departureCount: number }) {
  const key = `${item.item_id}`;
  useEffect(
    () =>
      onPage(`view_item:${key}`, () => {
        track("view_item", {
          ...itemsCurrency([item]),
          ...(item.price !== undefined ? { value: item.price } : {}),
          departure_count: departureCount,
          items: [item],
        });
        const code = takeCodeSearch();
        if (code) {
          const result: CodeSearchResult = new URLSearchParams(location.search).has("kalkis") ? "departure" : "exact";
          track("tour_code_search", { tour_code: code, result });
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, departureCount],
  );
  return null;
}

/**
 * Tur listesi: view_item_list (bu sayfada görünen kartlar, DOM'dan: [data-ga-list] içindeki [data-ga-item]) ve filtre
 * varsa view_search_results (+ kodla aramada tour_code_search partial/none).
 */
export function TrackListView({
  list,
  search,
}: {
  list: { id: string; name: string };
  search: (Omit<EventMap["view_search_results"], "search_term" | "tour_code"> & { q?: string; kod?: string }) | null;
}) {
  const key = `${list.id}:${JSON.stringify(search)}`;
  useEffect(
    () =>
      onPage(`list:${key}`, () => {
        const root = document.querySelector(`[data-ga-list-id="${CSS.escape(list.id)}"]`);
        const items: AnalyticsItem[] = [];
        root?.querySelectorAll("[data-ga-item]").forEach((el, i) => {
          try {
            const item = JSON.parse(el.getAttribute("data-ga-item") ?? "") as AnalyticsItem;
            items.push({ ...item, index: Number(el.getAttribute("data-ga-index") ?? i + 1), item_list_id: list.id, item_list_name: list.name });
          } catch {
            // bozuk öğe atlanır
          }
        });
        if (items.length > 0) track("view_item_list", { item_list_id: list.id, item_list_name: list.name, ...itemsCurrency(items), items });
        if (search) {
          const { q, kod, ...rest } = search;
          const code = cleanCode(kod);
          track("view_search_results", { ...rest, search_term: scrubText(q) ?? code, tour_code: code });
          if (code) {
            takeCodeSearch();
            track("tour_code_search", { tour_code: code, result: search.result_count > 0 ? "partial" : "none" });
          }
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
  return null;
}

/** Yalnızca sayfanın yolunu taşıyan olaylar: 404, hata sınırı, tur verisi alınamıyor */
export function TrackPathEvent({ name, digest }: { name: "page_not_found" | "api_error" | "tours_unavailable"; digest?: string }) {
  useEffect(
    () =>
      onPage(name, () => {
        const page_path = location.pathname.slice(0, 200);
        if (name === "api_error") track("api_error", { page_path, error_digest: digest });
        else track(name, { page_path });
      }),
    [name, digest],
  );
  return null;
}

/** Arama gönderimi (ana sayfa, filtre paneli): istemci bileşenleri çağırır */
export function trackSearch(location: EventMap["search"]["search_location"], params: URLSearchParams) {
  const code = cleanCode(params.get("kod"));
  if (code) rememberCodeSearch(code);
  const dest = params.get("destinasyon") ?? "";
  const [kind, value] = dest.split(":");
  track("search", {
    search_location: location,
    search_term: scrubText(params.get("q")) ?? code,
    region: params.get("bolge") ?? (kind === "bolge" ? value : undefined),
    destination: params.get("kategori") ?? (kind === "kategori" ? value : undefined),
    month: params.get("ay") ?? undefined,
    family: params.get("aile") ?? undefined,
    tour_code: code,
  });
}

/**
 * Altbilginin "Çerez tercihleri" bağlantısı: JavaScript ile tercih penceresini açar; JavaScript yoksa (izleme de yok)
 * çerez politikası sayfasına gider.
 */
export function CookieSettingsLink({ href, className, children }: { href: string; className?: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className={className}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        openPreferences();
      }}
    >
      {children}
    </a>
  );
}
